import React, { useState, useEffect, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  format,
  addDays,
  startOfDay,
  endOfDay,
  differenceInDays,
  eachDayOfInterval,
  isAfter,
} from "date-fns";
import calendar_icon from "../../../assets/images/calendar.svg";
import "./CalendarComponent.css";

const CalendarComponent = ({
  onStartDateChange,
  onEndDateChange,
  startDate,
  endDate,
  disabled = false,
  type = "event", // 'event' or 'registration'
}) => {
  const today = new Date();
  const maxFutureDate = addDays(today, 14);

  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  const [open, setOpen] = useState(false);
  const calendarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(window.innerWidth < 480);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  // Get disabled dates based on selected start date and type
  const getDisabledDates = () => {
    if (type === "registration" && dateRange[0].startDate) {
      // For registration, disable dates beyond 14 days from selected start date
      const maxEndDate = addDays(dateRange[0].startDate, 14);
      return eachDayOfInterval({
        start: addDays(maxEndDate, 1),
        end: addDays(maxEndDate, 365), // Arbitrary future date
      });
    } else if (type === "event" && dateRange[0].startDate) {
      // For event type:
      // If past date selected: disable beyond selected date + 14
      // If future date selected: maintain today + 14 limit
      const selectedDate = dateRange[0].startDate;
      const maxDate = isAfter(selectedDate, today)
        ? maxFutureDate // Keep original today + 14 limit for future dates
        : addDays(selectedDate, 14); // 14 days from selected date for past dates

      return eachDayOfInterval({
        start: addDays(maxDate, 1),
        end: addDays(maxDate, 365), // Arbitrary future date
      });
    }
    return [];
  };

  // Sync internal state with props
  useEffect(() => {
    setDateRange([
      {
        startDate: startDate || null,
        endDate: endDate || null,
        key: "selection",
      },
    ]);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setOpen(false);
        if (!startDate && !endDate) {
          setDateRange([
            {
              startDate: null,
              endDate: null,
              key: "selection",
            },
          ]);
        }
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, startDate, endDate]);

  const handleSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;

    // If only start date is selected
    if (startDate && !endDate) {
      const newRange = {
        startDate,
        endDate: startDate,
        key: "selection",
      };
      setDateRange([newRange]);
      onStartDateChange(startOfDay(startDate));
      return;
    }

    // Prevent end date being before start date
    if (endDate < startDate) return;

    if (type === "registration") {
      // For registration type:
      // 1. Ensure dates don't exceed today
      if (startDate > today || endDate > today) {
        return;
      }

      // 2. Enforce 14-day limit from start date
      const daysDifference = differenceInDays(endDate, startDate);
      if (daysDifference > 14) {
        const maxEndDate = addDays(startDate, 14);
        const newRange = {
          startDate,
          endDate: maxEndDate,
          key: "selection",
        };
        setDateRange([newRange]);
        onStartDateChange(startOfDay(startDate));
        onEndDateChange(endOfDay(maxEndDate));
        setOpen(false);
        return;
      }
    } else {
      // For event type:
      // If selecting future date, maintain today + 14 limit
      // If selecting past date, enforce 14-day limit from selected date
      const maxDate = isAfter(startDate, today)
        ? maxFutureDate
        : addDays(startDate, 14);

      if (endDate > maxDate) {
        const newRange = {
          startDate,
          endDate: maxDate,
          key: "selection",
        };
        setDateRange([newRange]);
        onStartDateChange(startOfDay(startDate));
        onEndDateChange(endOfDay(maxDate));
        setOpen(false);
        return;
      }
    }

    setDateRange([ranges.selection]);
    onStartDateChange(startOfDay(startDate));
    onEndDateChange(endOfDay(endDate));
  };

  const handleOpen = () => {
    if (disabled) return;

    if (!dateRange[0].startDate) {
      setDateRange([
        {
          startDate: today,
          endDate: today,
          key: "selection",
        },
      ]);
    }
    setOpen(true);
  };

  return (
    <div
      className={`calendarWrapper ${disabled ? "disabled" : ""}`}
      ref={calendarRef}
    >
      <div className="inputContainer">
        <div className="custom-date-wrapper">
          <input
            type="text"
            className="custom-date-input"
            value={
              dateRange[0].startDate
                ? format(dateRange[0].startDate, "dd/MM/yyyy")
                : ""
            }
            onClick={handleOpen}
            readOnly
            placeholder="Start Date"
            disabled={disabled}
          />
          <img
            src={calendar_icon}
            alt="calendar"
            className="custom-date-icon"
          />
        </div>
        <div className="custom-date-wrapper">
          <input
            type="text"
            className="custom-date-input"
            value={
              dateRange[0].endDate
                ? format(dateRange[0].endDate, "dd/MM/yyyy")
                : ""
            }
            onClick={handleOpen}
            readOnly
            placeholder="End Date"
            disabled={disabled}
          />
          <img
            src={calendar_icon}
            alt="calendar"
            className="custom-date-icon"
          />
        </div>
      </div>
      {open && (
        <div className="pickerContainer">
          <DateRangePicker
            onChange={handleSelect}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            months={isMobile ? 1 : 2}
            ranges={dateRange}
            direction={isMobile ? "vertical" : "horizontal"}
            maxDate={type === "registration" ? today : maxFutureDate}
            disabledDates={getDisabledDates()}
            showDateDisplay={false}
            editableDateInputs={false}
            rangeColors={["#d8edfe"]}
            showPreview={true}
            dragSelectionEnabled={true}
          />
          <div className="buttonContainer">
            <button
              className="closeButton"
              onClick={() => {
                setOpen(false);
                if (!startDate && !endDate) {
                  setDateRange([
                    {
                      startDate: null,
                      endDate: null,
                      key: "selection",
                    },
                  ]);
                }
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarComponent;
